/* eslint-disable max-len */

import { FC, RefObject, useState } from 'react';
import { Dropdown, Menu, Popconfirm, Tooltip, message } from 'antd';
import { DeleteOutlined, EditOutlined, LinkOutlined, MoreOutlined } from '@ant-design/icons';
import { UserResponse } from 'types';
import { LuReply } from 'react-icons/lu';
import { BiChat } from 'react-icons/bi';
import { getMentionUuidFromNode } from 'utils/string.utils';
import type { RichTextEditorHandle } from 'components/RichTextEditor';
import { UploadFileApi } from '../../types/upload-type';
import CommentReactionsCounter from './CommentReactionsCounter';
import CommentReactionOptions from './CommentReactionOptions';
import CommentQuickReactions from './CommentQuickReactions';
import styles from './styles.module.less';

interface CommentActionsBaseProps {
  comment: any;
  user: UserResponse;
  images: UploadFileApi[];
  type: 'tasks' | 'projects' | 'requests';
  typeUuid: string;
  loadingImages: number;
  editing: boolean;
  quotedRemoved?: boolean;
  editorText: RefObject<RichTextEditorHandle>;
  onReplyTo: (replyToUuid: string, replyTo: any, creator?: any) => void;
  onQuoteTo: (uuid: string, comment: any) => void;
  onEdit: (edit: boolean) => void;
}

export interface CommentActionsParentProps extends CommentActionsBaseProps {
  parentEl?: undefined;
  onAddReaction: (uuid: string, reaction: any) => void;
  onRemoveReaction: (uuid: string, reaction: any) => void;
  onUpdate: (uuid: string, values: any, uploads: UploadFileApi[], onFinish: () => void) => Promise<void>;
  onUpdateSchedule?: (uuid: string, values: string, onFinish: () => void) => Promise<void>;
  onDelete: (uuid: string) => Promise<void>;
}

export interface CommentActionsChildrenProps extends CommentActionsBaseProps {
  parentEl: Record<string, any>;
  onAddReaction: (parentUuid: string, uuid: any, reaction: any) => void;
  onRemoveReaction: (parentUuid: string, uuid: any, reaction: any) => void;
  onUpdate: (uuid: string, parentUuid: string, values: any, uploads: UploadFileApi[], onFinish: () => void) => Promise<void>;
  onUpdateSchedule?: (uuid: string, values: string, onFinish: () => void) => Promise<void>;
  onDelete: (uuid: string, parentUuid: string) => Promise<void>;
}

export type CommentActionsProps = CommentActionsParentProps | CommentActionsChildrenProps;

const CommentActions: FC<CommentActionsProps> = (props) => {
  const { comment, user, type, typeUuid, editing, quotedRemoved, editorText, onReplyTo, onQuoteTo, onEdit, images } = props;

  const isOwner = user?.uuid === comment.creator.uuid;
  const [isReactionPopoverOpen, setIsReactionPopoverOpen] = useState(false);
  const [isMoreDropdownOpen, setIsMoreDropdownOpen] = useState(false);
  const [isDeleteConfirmOpen, setIsDeleteConfirmOpen] = useState(false);

  const isBarPinned = isReactionPopoverOpen || isMoreDropdownOpen || isDeleteConfirmOpen;

  const handleSave = () => {
    if (props.loadingImages) return;

    const newComment = editorText.current?.getValue();
    const commentDelta = JSON.stringify(editorText.current?.getQuill()?.getEditor().getContents());

    if (!newComment || editorText.current?.isEmpty()) return;

    const values: Record<string, any> = {
      text: newComment,
      text_delta: commentDelta,
      related_users: getMentionUuidFromNode(newComment),
      uploads: [
        ...comment.uploads.map((u: any) => u.uuid),
        ...images.filter((el) => el.response).map((el) => el.response?.uuid),
      ],
    };

    if (quotedRemoved) {
      values.quoted_id = null;
    }

    if (props.parentEl) {
      props.onUpdate(comment.uuid, props.parentEl.uuid, values, images, () => onEdit(false));
    } else {
      props.onUpdate(comment.uuid, values, images, () => onEdit(false));
    }
  };

  const handleDelete = async () => {
    if (props.parentEl) {
      await props.onDelete(comment.uuid, props.parentEl.uuid);
    } else {
      await props.onDelete(comment.uuid);
    }
  };

  const handleCopyLink = () => {
    const url = new URL(window.location.href);

    if (type === 'tasks') {
      url.searchParams.set('task_uuid', typeUuid);
      url.searchParams.set('task_comment_uuid', comment.uuid);
    } else if (type === 'requests') {
      url.searchParams.set('target_comment_id', comment.uuid);
    } else {
      url.searchParams.set('target_comment_id', comment.uuid);
    }

    navigator.clipboard.writeText(url.toString());
    message.success('Link copied!');
    setIsMoreDropdownOpen(false);
  };

  const handleReplyInThread = () => {
    const mentionReply = type !== 'tasks' || comment.creator.type !== 'contact';
    let targetReply = comment.creator.uuid !== user.uuid ? comment.creator : null;
    if (!targetReply && props.parentEl && props.parentEl.creator.uuid !== user.uuid) {
      targetReply = props.parentEl.creator;
    }
    onReplyTo((props.parentEl ?? comment).uuid, comment, mentionReply ? targetReply : null);
  };

  const addReactionHandler = (reaction: any) => {
    if (props.parentEl) {
      props.onAddReaction(props.parentEl.uuid, comment.uuid, reaction);
    } else {
      props.onAddReaction(comment.uuid, reaction);
    }
  };

  const removeReactionHandler = (reaction: any) => {
    if (props.parentEl) {
      props.onRemoveReaction(props.parentEl.uuid, comment.uuid, reaction);
    } else {
      props.onRemoveReaction(comment.uuid, reaction);
    }
  };

  const moreMenu = (
    <Menu>
      {!comment.send_at && (
        <Menu.Item
          key="quote"
          icon={<LuReply size={16} />}
          onClick={() => { onQuoteTo(comment.uuid, comment); setIsMoreDropdownOpen(false); }}
        >
          Quote in reply
        </Menu.Item>
      )}
      {!comment.send_at && (
        <Menu.Item key="copy-link" icon={<LinkOutlined style={{ fontSize: 16 }} />} onClick={handleCopyLink}>
          Copy link
        </Menu.Item>
      )}
      {isOwner && !comment.deleted_at && [
        <Menu.Divider key="divider" />,
        <Menu.Item key="delete" style={{ color: '#ff4d4f' }} className="no-hover-highlight">
          <Popconfirm
            cancelText="No"
            okText="Yes"
            onConfirm={handleDelete}
            placement="bottom"
            title="Do you want to delete this comment?"
            visible={isDeleteConfirmOpen}
            onVisibleChange={(v) => {
              setIsDeleteConfirmOpen(v);
              if (!v) setIsMoreDropdownOpen(false);
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#ff4d4f' }}>
              <DeleteOutlined style={{ fontSize: 16 }} />
              <span>Delete</span>
            </div>
          </Popconfirm>
        </Menu.Item>,
      ]}
    </Menu>
  );

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'end', gap: 12, marginTop: 6 }}>
        {editing && (
          <button
            type="button"
            className="primary default-button"
            style={{ padding: 0, marginTop: 6 }}
            onClick={handleSave}
          >
            Save
          </button>
        )}

        {editing && (
          <button
            type="button"
            className="red default-button"
            style={{ padding: 0, marginTop: 6 }}
            onClick={() => {
              onEdit(false);
              editorText.current?.updateValue(comment.text ?? '');
            }}
          >
            Cancel
          </button>
        )}
      </div>

      {!editing && !comment.send_at && (
        <div
          className={styles.reactionsRow} style={{ minHeight: '34px' }} onClick={(e) => e.stopPropagation()}>
          <CommentReactionsCounter
            data={comment}
            onAddReaction={addReactionHandler}
            onRemoveReaction={removeReactionHandler}
          >
            {!comment.deleted_at && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <CommentReactionOptions
                  data={comment}
                  onAddReaction={addReactionHandler}
                  onRemoveReaction={removeReactionHandler}
                  onVisibleChange={setIsReactionPopoverOpen}
                />
                <button
                  type="button"
                  className={`default-button ${styles.actionHoverContainer}`}
                  onClick={handleReplyInThread}
                >
                  Reply
                </button>
              </div>
            )}
          </CommentReactionsCounter>

        </div>
      )}

      {!editing && !comment.deleted_at && (
        <div
          className={`${isBarPinned ? '' : styles.actionHoverContainer} ${styles.actionButtonsContainer}`}
          style={isBarPinned ? { display: 'flex' } : undefined}
          onClick={(e) => e.stopPropagation()}
        >
            {!comment.send_at && (
              <div className={styles.actionButtonsWrapper}>
                <CommentQuickReactions
                  comment={comment}
                  parentEl={props.parentEl}
                  onAddReaction={props.onAddReaction}
                  onRemoveReaction={props.onRemoveReaction}
                />
              </div>
            )}

          <div className={styles.actionButtonsWrapper}>
            {!comment.send_at && (
              <CommentReactionOptions
                data={comment}
                onAddReaction={addReactionHandler}
                onRemoveReaction={removeReactionHandler}
                onVisibleChange={setIsReactionPopoverOpen}
                alwaysShow
              />
            )}

            {isOwner && (
              <Tooltip title="Edit">
                <button
                  type="button"
                  className={`default-button ${styles.actionBarIconButton}`}
                  onClick={() => onEdit(true)}
                >
                  <EditOutlined style={{ fontSize: 16 }} />
                </button>
              </Tooltip>
            )}

            {!comment.send_at && (
              <Tooltip title="Reply in thread">
                <button
                  type="button"
                  className={`default-button ${styles.actionBarIconButton}`}
                  onClick={handleReplyInThread}
                >
                  <BiChat size={16} />
                </button>
              </Tooltip>
            )}

            <Dropdown
              overlay={moreMenu}
              trigger={['click']}
              placement="bottomRight"
              visible={isMoreDropdownOpen || isDeleteConfirmOpen}
              onVisibleChange={(v) => {
                if (!isDeleteConfirmOpen) setIsMoreDropdownOpen(v);
              }}
            >
              <Tooltip title="More actions">
                <button
                  type="button"
                  className={`default-button ${styles.actionBarIconButton}`}
                >
                  <MoreOutlined style={{ fontSize: 16 }} />
                </button>
              </Tooltip>
            </Dropdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default CommentActions;
